// Duplicate review (`#candidates/duplicates/<cm_id>`, DS DC-85 / candidate C-8): "The duplicate-review work item opens
// a *Duplicate review* page with the same grid." The Action Center item of the owning Recruiter and the Admin pool
// links here; "Expose a duplicate-review work item in the Recruiter and TA Admin Action Center." (his TA Admin is the
// Admin since DC-228) Both profiles side by
// side, the matched attributes gold, a profile the reader may not open masked whole. *Create distinct* keeps both with
// a reason (a TA role's choice is also the pool's review); *Use existing* is the merge into the existing profile, which
// "Admin manages merges/recovery." — so it opens the Merge dialog for the Admin and is blocked for everyone else with
// that sentence. Nothing merges here. A resolved review shows its resolution and who resolved it, never the BRD 4.1
// requirement line as its subtitle (UAT F-32, Q-4's default).
// The page's meta line carries each Candidate ID the reader may open (and the decision's day once resolved); the grid's
// subtitle is the raising source, the door the new profile came through (UAT D-22, candidate C-8), never an engineering
// sentence, and no standing banner sits over the grid ("Never auto-merge." stays the reason field's hint).
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useState } from "react";
import type { CandidateDuplicateOpenOutput } from "@/api/tools";
import { candidateDuplicateOpen, candidateResolveDuplicate } from "@/api/tools";
import { isClientRefusal, useWrite } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Button, Card, CheckTile, Compare, DIALOG_LABELS, EmptyState, ErrorStrip, Field, Flex, G1_LABELS, G6_LABELS, G6_RULE_LABELS,
  NAV_ITEM_LABELS, PageHeader, RULE_LABELS, Skeleton, Stack, TextArea, TextLink,
} from "@/mc";
import { BRD } from "./brd";
import { useSettledRead, withQuery } from "@/views/shared/settledRead";
import { PROFILE_LABELS as P } from "./labels";
import { duplicateAttributes, profileHref, STATUS_WORD } from "./profile";
import { refusalSentence } from "./refusals";

export function DuplicateReviewPage({ matchId }: { matchId: string }) {
  return <ReviewBody matchId={matchId} />;
}

function ReviewBody({ matchId }: { matchId: string }) {
  const route = useRoute();
  const { register } = useViewState();
  const read = useSettledRead(["candidate-duplicate", matchId], () => candidateDuplicateOpen({ candidate_match_id: matchId }));
  useEffect(() => register({ view: "candidates", ids: { match: matchId }, summary: { status: read.data?.status ?? "loading" } }), [register, matchId, read.data?.status]);
  if (read.error && isClientRefusal(read.error)) return <NotFound route={route.raw} />;
  return (
    <Stack gap={16}>
      <Flex gap={8}><TextLink href="#candidates" >{NAV_ITEM_LABELS.candidates}</TextLink></Flex>
      <DuplicateHeader review={read.data ?? null} loading={read.isLoading} />
      {read.error ? (
        <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: read.error.status, refusal_code: read.error.code, request_id: read.error.requestId }} onRetry={() => void read.refetch()}  />
      ) : null}
      {!read.data ? (read.error ? null : <Skeleton lines={8} height={18} />) : <DuplicateReview review={read.data} />}
    </Stack>
  );
}

/** The door that raised the review, in his words: a profile saved through Add candidate (decided at save, or referred
 *  by a seat that may not decide) or added on a requisition. A door he has not named yet draws nothing. */
export function raisingSource(origin: string): string | undefined {
  return origin === "create" || origin === "refer" || origin === "add" ? DIALOG_LABELS.addCandidate : undefined;
}

/** *Duplicate review* with its meta line: each Candidate ID the reader may open, and the decision's day once resolved. */
export function DuplicateHeader({ review, loading }: { review: CandidateDuplicateOpenOutput | null; loading: boolean }) {
  const meta = review ? [
    ...review.sides.filter((side) => side.projection !== "masked").map((side) => <span key={side.side} className="mc-mono">{side.candidate_no}</span>),
    ...(review.decided_at ? [<span key="decided">{review.decided_at.slice(0, 10)}</span>] : []),
  ] : undefined;
  return <PageHeader title={P.duplicateReview} loading={loading} meta={meta} />;
}

export function DuplicateReview({ review }: { review: CandidateDuplicateOpenOutput }) {
  const { can } = useSession();
  const [distinct, setDistinct] = useState(false);
  const [reason, setReason] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const resolve = useWrite(
    (input: Parameters<typeof candidateResolveDuplicate>[0], key) => candidateResolveDuplicate(input, key),
    [["candidate-duplicate"], ["candidate-profile"], ["candidate-timeline"]],
  );
  const probe = review.sides.find((side) => side.side === "probe");
  const existing = review.sides.find((side) => side.side === "matched");
  const mayMerge = can("candidate.merge") && review.status !== "merged" && review.status !== "dismissed"
    && Boolean(probe?.candidate_id && existing?.candidate_id) && review.sides.every((side) => side.status === "Active");
  const mergeHref = probe?.candidate_id && existing?.candidate_id
    ? withQuery(profileHref({ token: existing.candidate_id }), { action: "merge", with: probe.candidate_id })
    : null;
  const records = review.sides.map((side) => ({ id: side.candidate_no, name: side.display_name ?? "", status: STATUS_WORD[side.status], masked: side.projection === "masked" }));

  if (!review.may_resolve) {
    const kept = review.status === "merged" ? existing : probe;
    return (
      <Card>
        <EmptyState
          icon="check"
          sub={[review.decision_reason, review.decided_at?.slice(0, 10)].filter(Boolean).join(" · ") || undefined}
          action={kept?.candidate_id ? <TextLink href={profileHref({ token: kept.candidate_id })} >{kept.candidate_no}</TextLink> : undefined}
        >
          {review.status === "merged" ? STATUS_WORD.Merged : review.status === "kept_distinct" ? G6_LABELS.createDistinct : P.duplicateReview}
        </EmptyState>
        {mayMerge && mergeHref ? (
          <Flex justify="end"><Button tone="ghost" onClick={() => go(mergeHref)} >{ACTION_LABELS.merge}</Button></Flex>
        ) : null}
      </Card>
    );
  }

  return (
    <Card
      title={G6_LABELS.potentialMatches}
      sub={raisingSource(review.origin)}
      actions={mayMerge && mergeHref ? <Button tone="ghost" sm onClick={() => go(mergeHref)} >{ACTION_LABELS.merge}</Button> : undefined}
      refused={refused ? { reason: refused } : undefined}
    >
      <Stack gap={12}>
        <Compare
          records={records}
          attributes={duplicateAttributes(review)}
          useExisting={{
            onClick: mayMerge && mergeHref ? () => go(mergeHref) : undefined,
            blocked: mayMerge ? undefined : { reason: BRD.adminMerges },
            hs: "cand.dup.use",
          }}
          createDistinct={{ onClick: () => setDistinct(true), hs: "cand.dup.distinct" }}
        />
        {distinct ? (
          <>
            <CheckTile checked={distinct} onChange={setDistinct} >{G6_LABELS.createDistinct}</CheckTile>
            <Field label={G1_LABELS.reason} required hint={G6_RULE_LABELS.neverAutoMerge}>
              <TextArea rows={2} value={reason} onChange={(event) => setReason(event.target.value)}  />
            </Field>
            <Flex justify="end">
              <Button
                tone="primary"
                blocked={reason.trim().length >= 3 ? undefined : { reason: G6_RULE_LABELS.neverAutoMerge }}
                busy={resolve.isPending}
                onClick={() => {
                  setRefused(null);
                  resolve.mutateAsync({ candidate_match_id: review.candidate_match_id, resolution: "kept_distinct", reason: reason.trim() })
                    .catch((error: unknown) => setRefused(refusalSentence(error) ?? RULE_LABELS.failedSyncRetryable));
                }}
                
              >
                {ACTION_LABELS.save}
              </Button>
            </Flex>
          </>
        ) : null}
        <Flex gap={8} wrap>
          {review.sides.map((side) => side.candidate_id ? (
            <TextLink key={side.side} href={profileHref({ token: side.candidate_id })} >{side.display_name ?? side.candidate_no}</TextLink>
          ) : null)}
        </Flex>
      </Stack>
    </Card>
  );
}
